import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  Paper,
  PaperProps,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import Draggable from "react-draggable";

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface IModel {
  persistent?: boolean;
  title?: string | React.ReactNode;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  sxModal?: SxProps<Theme>;
  sxContent?: SxProps<Theme>;
  loading?: boolean;
  hiddenHeader?: boolean;
  fullScreen?: boolean;
  hideCloseButton?: boolean;
}
export const ModalContent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <>{children}</>;
export const ModalHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <>{children}</>;
export const ModalFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <>{children}</>;

export default function Modal({
  persistent,
  title: Title,
  onClose,
  open,
  children,
  sxModal,
  sxContent,
  loading,
  fullScreen,
  hiddenHeader,
  hideCloseButton = false,
}: IModel) {
  const handleClose = () => {
    onClose();
  };
  let header: React.ReactNode = null;
  let content: React.ReactNode = null;
  let footer: React.ReactNode = null;

  const theme = useTheme();

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === ModalHeader) {
        header = child;
      } else if (child.type === ModalContent) {
        content = child;
      } else if (child.type === ModalFooter) {
        footer = child;
      }
    }
  });

  return (
    <BootstrapDialog
      open={open}
      fullScreen={fullScreen}
      PaperComponent={PaperComponent}
      PaperProps={{ sx: sxModal }}
      sx={{ p: 0 }}
      onClose={() => (persistent || loading ? 1 : handleClose())}
      aria-labelledby="draggable-dialog-title"
    >
      <Box sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
        <Backdrop sx={{ color: "inherit" }} open={!!loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
      <Box sx={{ padding: 1 }}>
        {!hiddenHeader && (
          <DialogTitle
            sx={{ m: 0, padding: "0px 8px", cursor: "move" }}
            id="draggable-dialog-title"
          >
            {header ? header : typeof Title === "string" ? Title : Title}
            <Divider sx={{ my: 1 }} />
          </DialogTitle>
        )}

        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <DialogContent sx={sxContent}>{content}</DialogContent>
        {footer && (
          <DialogActions sx={{ cursor: "move" }} id="draggable-dialog-title">
            {footer}
          </DialogActions>
        )}
      </Box>
    </BootstrapDialog>
  );
}
